import { type AxiosError } from 'axios'
import { type UseQueryOptions, type UseQueryResult } from '@tanstack/react-query'

import { useApiGatewayHost } from '@dhl/api-gateway-provider'
import {
  type getParcelShopLocationsByKeys,
  type getParcelShopLocationsCountrycode,
  type getParcelShopLocationsCountrycodeId,
  type GetParcelShopLocationsCountrycodeParams,
  type ParcelShopLocationDto,
  useGetParcelShopLocationsByKeys,
  type UseGetParcelShopLocationsByKeysArgs,
  useGetParcelShopLocationsCountrycode,
  useGetParcelShopLocationsCountrycodeId,
} from '@dhl/servicepoint-locator-api'
import { type Servicepoint, type ServiceType } from './model.js'
import { mapParcelShopLocationToServicepoint } from './mapParcelShopLocationToServicepoint.js'

type UseFetchServicepointsCriteria = {
  country: string
  houseNumber?: string
  isLocker?: boolean
  limit?: number
  query?: string
  postalCode?: string
  serviceType?: ServiceType
  sameDepot?: boolean
}

type FetchServicePointArgs = {
  countryCode: string
  id: string
}

const fromServiceType = (input: ServiceType): 'parcel-first-mile' | 'parcel-last-mile' | '' => {
  switch (input) {
    case 'ParcelFirstMile':
      return 'parcel-first-mile'
    case 'ParcelLastMile':
      return 'parcel-last-mile'
    default:
      return ''
  }
}

const mapParcelShopLocationsToServicepoints = (parcelShopLocations: ParcelShopLocationDto[]): Servicepoint[] =>
  parcelShopLocations.map(mapParcelShopLocationToServicepoint)

const useSetApiLocatorBaseUrl = () => {
  const host = useApiGatewayHost()
  // reason for this is that the locator API lib expects the base URL to be set on the window.env object, which is a bit odd but we can work with it. This means that this hook needs to be used before any of the locator API hooks are used, to ensure that the base URL is set correctly.
  // @ts-ignore -- the locator API lib expects the base URL to be set on the window.env object
  if (!window.env) {
    // reason: see above
    // @ts-ignore
    window.env = {}
  }
  // reason: see above
  // @ts-ignore
  window.env.API_LOCATOR = host
}

export const useFetchServicepoint = <TData = Awaited<ReturnType<typeof getParcelShopLocationsCountrycodeId>>>(
  { countryCode, id }: FetchServicePointArgs,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getParcelShopLocationsCountrycodeId>>, AxiosError, TData>>
  }
): UseQueryResult<Servicepoint | undefined, Error | null> => {
  useSetApiLocatorBaseUrl()

  return useGetParcelShopLocationsCountrycodeId(countryCode, id, {
    // @ts-ignore
    query: {
      ...options?.query,
      select: (data) => (data ? mapParcelShopLocationToServicepoint(data) : undefined),
    },
  })
}

export const useFetchServicepoints = <TData = Awaited<ReturnType<typeof getParcelShopLocationsCountrycode>>>(
  { country, houseNumber, isLocker, limit, postalCode, query, sameDepot, serviceType }: UseFetchServicepointsCriteria,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getParcelShopLocationsCountrycode>>, AxiosError, TData>>
  }
): UseQueryResult<Servicepoint[] | undefined, Error | null> => {
  useSetApiLocatorBaseUrl()

  const params: GetParcelShopLocationsCountrycodeParams = {
    houseNumber,
    postalCode,
    sameDepot,
    serviceType: serviceType ? [fromServiceType(serviceType)] : undefined,
    limit: limit ?? 5,
    q: query,
  }

  if (typeof isLocker !== 'undefined') {
    params.isLocker = isLocker
  }

  return useGetParcelShopLocationsCountrycode(country, params, {
    query: {
      // reason TODO: why setup like this?
      // @ts-ignore
      enabled: !!country && !!postalCode && (options?.query?.enabled ?? true),
      select: (data) => mapParcelShopLocationsToServicepoints(data),
    },
  })
}

export const useFetchMultipleServicepoints = (
  { countryCode, postalCode, servicePointIds }: UseGetParcelShopLocationsByKeysArgs,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getParcelShopLocationsByKeys>>, AxiosError, Servicepoint[] | undefined>
    >
  }
): UseQueryResult<Servicepoint[] | undefined, AxiosError> => {
  useSetApiLocatorBaseUrl()

  return useGetParcelShopLocationsByKeys(
    { countryCode, postalCode, servicePointIds },
    {
      query: {
        // reason TODO: why setup like this?
        // @ts-ignore
        enabled: !!countryCode && !!postalCode && servicePointIds.length > 0 && (options?.query?.enabled ?? true),
        select: (data) => mapParcelShopLocationsToServicepoints(data.results),
      },
    }
  )
}

type FetchServicepointByIdArgs = {
  countryCode: string
  id: string
}

export const useFetchServicepointById = (
  { countryCode, id }: FetchServicepointByIdArgs,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getParcelShopLocationsCountrycodeId>>,
        AxiosError,
        Servicepoint | undefined
      >
    >
  }
): UseQueryResult<Servicepoint | undefined, AxiosError | null> => {
  useSetApiLocatorBaseUrl()

  return useGetParcelShopLocationsCountrycodeId(countryCode, id, {
    query: {
      // @ts-ignore
      enabled: !!countryCode && !!id && (options?.query?.enabled ?? true),
      staleTime: 5 * 60 * 1000, // 5 minutes
      select: (data) => mapParcelShopLocationToServicepoint(data),
    },
  })
}
